/**
 * Structured editor for a project's execution routes.
 *
 * Renders the route lists for each role (refiner, implementer, reviewer) with the shared
 * `RouteListEditor` and hands the rebuilt routes back through `onChange`. A row's Machine
 * column maps onto the stored shape: rows for "All machines" are the role's general list;
 * rows naming a machine are that machine's section for the role, which replaces the general
 * list there. Each role shows two lists with the same mapping: `routes` and the optional
 * `light_routes` a small ticket tries first.
 */
import { EXECUTION_ROLES, type ExecutionRole, type ExecutionSection } from '@nightshift/config';

import { RouteListEditor, type RouteMachine } from '../components/route-list-editor.tsx';
import { Kicker } from '../components/primitives.tsx';
import { rowsForRole, sectionWithRole, type RouteListKey } from '../lib/execution-rows.ts';
import type { RouteRow } from '../lib/route-list.ts';
import type { CatalogueModel } from '../live/api.ts';

/** Kept for hosts that built their machine list against the earlier name. */
export type EditorMachine = RouteMachine;

const ROLE_COPY: Record<ExecutionRole, { label: string; help: string }> = {
  refiner: {
    label: 'Refiner',
    help: 'Refining: reads the brief, researches, plans and writes acceptance criteria.',
  },
  implementer: {
    label: 'Implementer',
    help: 'Implementing: writes the code and opens the merge request.',
  },
  reviewer: {
    label: 'Reviewer',
    help: 'AI Review: every reviewer named by the ticket type runs on these routes.',
  },
};

interface ExecutionRoutesEditorProps {
  execution: ExecutionSection;
  onChange: (next: ExecutionSection) => void;
  machines: RouteMachine[];
  models: CatalogueModel[];
  disabled?: boolean;
}

export function ExecutionRoutesEditor({
  execution,
  onChange,
  machines,
  models,
  disabled = false,
}: ExecutionRoutesEditorProps) {
  // The row helpers drop emptied role tables; the stored document keeps every role, empty
  // meaning "inherit", so the result is completed before it goes back to the draft.
  const change = (role: ExecutionRole, rows: RouteRow[], list: RouteListKey) => {
    const next = sectionWithRole(execution, role, rows, list);
    onChange({
      ...next,
      refiner: next.refiner ?? { routes: [] },
      implementer: next.implementer ?? { routes: [] },
      reviewer: next.reviewer ?? { routes: [] },
      machines: next.machines ?? {},
    });
  };
  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker>Execution routes</Kicker>
        <span className="cfg-help" style={{ marginLeft: 'auto' }}>
          Order is the fallback order. A row for one machine replaces the All-machines rows on that machine.
        </span>
      </header>
      <div className="sheet-panel-body">
        {EXECUTION_ROLES.map((role) => (
          <div className="route-block" key={role}>
            <div className="route-block-head">
              <Kicker>{ROLE_COPY[role].label}</Kicker>
              <span className="cfg-help">{ROLE_COPY[role].help}</span>
            </div>
            <RouteListEditor
              rows={rowsForRole(execution, role)}
              onChange={(rows) => change(role, rows, 'routes')}
              machines={machines}
              models={models}
              disabled={disabled}
              label={`${ROLE_COPY[role].label} route`}
              anyMachineLabel="All machines"
              emptyText="No routes here. This stage inherits the default agent routes under Nightshift → Models."
            />
            <div className="route-block-head route-block-sub">
              <span className="route-block-sub-title">Light routes</span>
              <span className="cfg-help">
                Tried first on a small ticket; the routes above take over after a failure, bounce or stall.
              </span>
            </div>
            <RouteListEditor
              rows={rowsForRole(execution, role, 'light_routes')}
              onChange={(rows) => change(role, rows, 'light_routes')}
              machines={machines}
              models={models}
              disabled={disabled}
              label={`${ROLE_COPY[role].label} light route`}
              anyMachineLabel="All machines"
              emptyText={
                rowsForRole(execution, role).length > 0
                  ? 'No light routes. Every ticket runs the routes above in this stage.'
                  : 'No light routes here. The Nightshift light routes apply while this stage inherits the default agent routes.'
              }
            />
          </div>
        ))}
      </div>
    </section>
  );
}
